import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Maintenance from "@/pages/Maintenance";
import ScrollToTop from "@/components/ScrollToTop";
import GlobalEscapeHandler from "@/components/GlobalEscapeHandler";
import { adminUrl, getAdminBase } from "@/lib/adminPath";
import Home from "@/pages/Home";
import Plans from "@/pages/Plans";
import Purchase from "@/pages/Purchase";
import Success from "@/pages/Success";
import VerifyMember from "@/pages/VerifyMember";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import About from "@/pages/About";
import Benefits from "@/pages/Benefits";
import WhyUs from "@/pages/WhyUs";
import Testimonials from "@/pages/Testimonials";
import HowItWorks from "@/pages/HowItWorks";
import Referral from "@/pages/Referral";
import Corporate from "@/pages/Corporate";
import JoinNPC from "@/pages/JoinNPC";
import { Privacy, Terms } from "@/pages/Legal";
import { V2Home, V2Plans, V2Corporate, V2About, V2Benefits, V2WhyUs, V2FAQ, V2Testimonials, V2Contact, V2Privacy, V2Terms } from "@/v2/V2Pages";
import V3Home from "@/pages/V3Home";
import Doctors from "@/pages/Doctors";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CustomerDashboard from "@/pages/CustomerDashboard";
import { AdminLayout, AdminDashboard, AdminLeads, AdminPlans, AdminMembers, AdminPayments, AdminRenewals, AdminCorporate, AdminEmployees, AdminReferrals, AdminInvoices, AdminUsers, AdminRoles, AdminEmployeePerformance, AdminPayouts, AdminVerify, AdminCheckin, AdminSettings, AdminLogs, AdminWhatsAppLogs, AdminBenefits, AdminVisits } from "@/pages/Admin";
import { canAccess, defaultAdminRoute } from "@/lib/permissions";

function ProtectedCustomer({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-4 border-[#1E4F87] border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Route-level permission gate for admin pages.
 * If the logged-in user's role does not permit `section`, redirect them to THEIR default landing.
 * Used inside the /admin nested routes.
 */
function RoleRoute({ section, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-4 border-[#1E4F87] border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to={adminUrl("/login")} replace />;
  if (!canAccess(section, user)) return <Navigate to={defaultAdminRoute(user.role)} replace />;
  return children;
}

const MAINT_CACHE_KEY = "npc:maint:v1";
const MAINT_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — long enough to dodge flicker, short enough to clear stale ON
const MAINT_BUST_KEY = "npc:maint:bust:v1"; // last-seen cache-bust token

function readCache() {
  try {
    const raw = localStorage.getItem(MAINT_CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || typeof c.ts !== "number") return null;
    if (Date.now() - c.ts > MAINT_CACHE_TTL_MS) return null;
    return c.data;
  } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(MAINT_CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); } catch {}
}

/**
 * Compare server cache-bust token vs the locally stored one.
 * - If no local token yet → store the server value silently (first visit baseline).
 * - If server token differs AND maintenance is currently OFF AND we haven't already
 *   reloaded for THIS token this session → hard-reload to flush stale in-memory state.
 * - Otherwise no-op.
 * Returns true when a reload was scheduled so callers can skip subsequent work.
 */
function maybeBustCache(data) {
  try {
    if (!data || data.maintenance_mode) return false; // only act when site is LIVE
    const serverToken = data.maintenance_cache_bust;
    if (!serverToken) return false;
    const localToken = localStorage.getItem(MAINT_BUST_KEY);
    if (!localToken) {
      localStorage.setItem(MAINT_BUST_KEY, serverToken);
      return false;
    }
    if (localToken === serverToken) return false;
    // Guard against reload loop: only reload once per token per tab session
    const reloadedFor = sessionStorage.getItem(MAINT_BUST_KEY);
    if (reloadedFor === serverToken) {
      localStorage.setItem(MAINT_BUST_KEY, serverToken);
      return false;
    }
    sessionStorage.setItem(MAINT_BUST_KEY, serverToken);
    localStorage.setItem(MAINT_BUST_KEY, serverToken);
    // Hard reload — clears React in-memory state + re-fetches HTML/JS bundles
    window.location.reload();
    return true;
  } catch { return false; }
}

function MaintenanceSplash() {
  // Minimal branded splash shown only on the first paint of a fresh visit while
  // /maintenance-status is in flight. Same gradient as the real page so the
  // visual transition is seamless.
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-[#0F2540] via-[#1E4F87] to-[#0F2540]"
      data-testid="maintenance-splash" />
  );
}

function MaintenanceGate({ children }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const path = location.pathname;
  // Honor configured admin path (default "/admin"). Customers must NEVER see splash on admin URLs.
  const adminBase = getAdminBase();
  const isAdminPath = path === adminBase || path.startsWith(adminBase + "/");
  // Initial state priority: pre-warmed window global → localStorage cache → null
  const [status, setStatus] = useState(() => {
    if (typeof window !== "undefined" && window.__NPC_MAINTENANCE__) return window.__NPC_MAINTENANCE__;
    return readCache();
  });
  const load = React.useCallback(() => {
    return api.get("/maintenance-status")
      .then((r) => {
        const data = r.data;
        if (maybeBustCache(data)) return; // reload scheduled — skip state update
        setStatus(data);
        writeCache(data);
      })
      .catch(() => setStatus((s) => s || { maintenance_mode: false }));
  }, []);
  useEffect(() => {
    let alive = true;
    // If still null on mount, await the pre-warm promise (cheap — usually already settled)
    if (status === null && typeof window !== "undefined" && window.__NPC_MAINTENANCE_INFLIGHT__) {
      window.__NPC_MAINTENANCE_INFLIGHT__.then((d) => {
        if (!alive) return;
        if (d) {
          if (maybeBustCache(d)) return;
          setStatus(d); writeCache(d);
        } else load();
      });
    } else {
      // Even with cached/pre-warmed status, do an initial network check so
      // we pick up a fresh cache-bust token immediately after a maintenance window.
      load();
    }
    const t = setInterval(() => alive && load(), 60000);
    return () => { alive = false; clearInterval(t); };
  }, [load]);
  // Admin paths are NEVER blocked or splashed — admin must always reach /admin/login fast
  if (isAdminPath) return children;
  // First-visit on a public path with no cached/pre-warmed status → splash instead of leaking the public app
  if (status === null) return <MaintenanceSplash />;
  const isAdminUser = !!user && ["super_admin","admin","staff","accounts"].includes(user.role);
  if (status.maintenance_mode) {
    // Admins bypass. If we don't yet know the role (auth still loading), keep the splash up
    // so we never flash the public app for a user who's actually an admin.
    if (isAdminUser) return children;
    if (authLoading) return <MaintenanceSplash />;
    return (
      <Maintenance
        message={status.maintenance_message}
        eta={status.maintenance_eta}
        etaAt={status.maintenance_eta_at}
        onExpire={load}
      />
    );
  }
  return children;
}

function AppRouter() {
  const adminBase = getAdminBase();
  const isCustomPath = adminBase !== "/admin";
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/plans" element={<Plans />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/referral" element={<Referral />} />
      <Route path="/corporate" element={<Corporate />} />
      <Route path="/join" element={<JoinNPC />} />
      <Route path="/purchase/:planId" element={<Purchase />} />
      <Route path="/success/:memberId" element={<Success />} />
      <Route path="/verify/:memberId" element={<VerifyMember />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/about" element={<About />} />
      <Route path="/benefits" element={<Benefits />} />
      <Route path="/doctors" element={<Doctors />} />
      <Route path="/why-us" element={<WhyUs />} />
      <Route path="/testimonials" element={<Testimonials />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* V2 design preview routes — sandboxed, do not affect production pages */}
      <Route path="/v2" element={<V2Home />} />
      <Route path="/v2/plans" element={<V2Plans />} />
      <Route path="/v2/corporate" element={<V2Corporate />} />
      <Route path="/v2/about" element={<V2About />} />
      <Route path="/v2/benefits" element={<V2Benefits />} />
      <Route path="/v2/why-us" element={<V2WhyUs />} />
      <Route path="/v2/faq" element={<V2FAQ />} />
      <Route path="/v2/testimonials" element={<V2Testimonials />} />
      <Route path="/v2/contact" element={<V2Contact />} />
      <Route path="/v2/privacy" element={<V2Privacy />} />
      <Route path="/v2/terms" element={<V2Terms />} />
      {/* V3 premium homepage preview — isolated, do not affect production pages */}
      <Route path="/v3" element={<V3Home />} />
      <Route path={`${adminBase}/login`} element={<Login admin />} />
      <Route path="/dashboard" element={<ProtectedCustomer><CustomerDashboard /></ProtectedCustomer>} />
      <Route path={adminBase} element={<AdminLayout />}>
        <Route index element={<RoleRoute section="dashboard"><AdminDashboard /></RoleRoute>} />
        <Route path="leads" element={<RoleRoute section="leads"><AdminLeads /></RoleRoute>} />
        <Route path="plans" element={<RoleRoute section="plans"><AdminPlans /></RoleRoute>} />
        <Route path="members" element={<RoleRoute section="members"><AdminMembers /></RoleRoute>} />
        <Route path="renewals" element={<RoleRoute section="renewals"><AdminRenewals /></RoleRoute>} />
        <Route path="payments" element={<RoleRoute section="payments"><AdminPayments /></RoleRoute>} />
        <Route path="corporate" element={<RoleRoute section="corporate"><AdminCorporate /></RoleRoute>} />
        <Route path="employees" element={<RoleRoute section="employees"><AdminEmployees /></RoleRoute>} />
        <Route path="referrals" element={<RoleRoute section="referrals"><AdminReferrals /></RoleRoute>} />
        <Route path="discount-codes" element={<RoleRoute section="referrals"><AdminReferrals /></RoleRoute>} />
        <Route path="employee-performance" element={<RoleRoute section="employee-performance"><AdminEmployeePerformance /></RoleRoute>} />
        <Route path="payouts" element={<RoleRoute section="payouts"><AdminPayouts /></RoleRoute>} />
        <Route path="verify" element={<RoleRoute section="verify"><AdminVerify /></RoleRoute>} />
        <Route path="checkin" element={<RoleRoute section="verify"><AdminCheckin /></RoleRoute>} />
        <Route path="visits" element={<RoleRoute section="visits"><AdminVisits /></RoleRoute>} />
        <Route path="plan-benefits" element={<RoleRoute section="plan-benefits"><AdminBenefits /></RoleRoute>} />
        <Route path="invoices" element={<RoleRoute section="invoices"><AdminInvoices /></RoleRoute>} />
        <Route path="logs" element={<RoleRoute section="admin-logs"><AdminLogs /></RoleRoute>} />
        <Route path="whatsapp-logs" element={<RoleRoute section="admin-logs"><AdminWhatsAppLogs /></RoleRoute>} />
        <Route path="settings" element={<RoleRoute section="settings"><AdminSettings /></RoleRoute>} />
        <Route path="users" element={<RoleRoute section="users"><AdminUsers /></RoleRoute>} />
        <Route path="roles" element={<RoleRoute section="roles"><AdminRoles /></RoleRoute>} />
      </Route>
      {/* Strict mode — when admin_path is customized, literal /admin AND /admin/* must 404 → home */}
      {isCustomPath && <Route path="/admin" element={<Navigate to="/" replace />} />}
      {isCustomPath && <Route path="/admin/*" element={<Navigate to="/" replace />} />}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <GlobalEscapeHandler />
          <MaintenanceGate>
            <AppRouter />
          </MaintenanceGate>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
